import { useState } from 'react';
import placeholderImg from '../assets/pixel-art/seeding.png';

// Define image variables with the placeholder
const sunflowerImg = placeholderImg;

// Create a singleton garden data service
const createGardenService = () => {
  // Storage key constants
  const PLANTS_STORAGE_KEY = 'gardenPlants';
  const REMINDERS_STORAGE_KEY = 'plantTaskReminders';
  const DIARY_ENTRIES_KEY = 'plantDiaryEntries';

  // Plant types definition
  const plantTypes = {
    tomato: { image: sunflowerImg, size: 48 }
  };

  // Maintain a subscribers list for components that need to be notified of changes
  let subscribers = [];
  
  // Load plants from localStorage or use empty array if not found
  const loadPlantsFromStorage = () => {
    try {
      const storedPlants = localStorage.getItem(PLANTS_STORAGE_KEY);
      return storedPlants ? JSON.parse(storedPlants) : [];
    } catch (error) {
      console.error("Error loading plants from localStorage:", error);
      return [];
    }
  };

  // Initialize plants from localStorage
  let plants = loadPlantsFromStorage();

  // Calculate plant age in weeks
  const calculatePlantAge = (startDate) => {
    const start = new Date(startDate);
    const today = new Date();
    const diffTime = Math.abs(today - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    return diffWeeks;
  };

  // Save plants to localStorage
  const savePlantsToStorage = () => {
    try {
      localStorage.setItem(PLANTS_STORAGE_KEY, JSON.stringify(plants));
      window.dispatchEvent(new Event('storage'));
    } catch (error) {
      console.error("Error saving plants to localStorage:", error);
    }
  };

  // Update plants and notify subscribers
  const setPlants = (newPlants) => {
    plants = newPlants;
    savePlantsToStorage();
    notifySubscribers();
  };

  // Add a new plant
  const addPlant = (newPlant) => {
    // Ensure plant type exists
    const plantType = newPlant.type.toLowerCase();
    if (!plantTypes[plantType]) {
      addPlantType(plantType, {
        image: newPlant.imageUrl || newPlant.icon_image_url || placeholderImg
      });
    }
    
    plants = [...plants, newPlant];
    savePlantsToStorage();
    notifySubscribers();
  };

  // Update a plant
  const updatePlant = (updatedPlant) => {
    plants = plants.map(plant => 
      plant.id === updatedPlant.id ? updatedPlant : plant
    );
    savePlantsToStorage();
    notifySubscribers();
  };

  const updatePlantProperty = (plantId, property, value) => {
    plants = plants.map(plant => 
      plant.id === plantId ? { ...plant, [property]: value } : plant
    );
    savePlantsToStorage();
    notifySubscribers();
  };

  // Remove a plant
  const removePlant = (plantId) => {
    plants = plants.filter(plant => plant.id !== plantId);
    savePlantsToStorage();
    clearPlantReminders(plantId);
    notifySubscribers();
  };

  // Subscribe to changes
  const subscribe = (callback) => {
    subscribers.push(callback);
    callback(plants); // Immediately notify with current state
    return () => {
      subscribers = subscribers.filter(sub => sub !== callback);
    };
  };

  // Notify all subscribers
  const notifySubscribers = () => {
    subscribers.forEach(callback => callback(plants));
  };

  // Get all plants
  const getPlants = () => plants;

  // Get reminders from localStorage
  const getReminders = () => {
    try {
      const storedReminders = localStorage.getItem(REMINDERS_STORAGE_KEY);
      return storedReminders ? JSON.parse(storedReminders) : [];
    } catch (error) {
      console.error("Error loading reminders from localStorage:", error);
      return [];
    }
  };

  // Save reminders to localStorage
  const saveReminders = (reminders) => {
    try {
      localStorage.setItem(REMINDERS_STORAGE_KEY, JSON.stringify(reminders));
      window.dispatchEvent(new Event('storage'));
    } catch (error) {
      console.error("Error saving reminders to localStorage:", error);
    }
  };

  // Add reminder
  const addReminder = (reminder) => {
    const reminders = getReminders();
    reminders.push(reminder);
    saveReminders(reminders);
  };

  // Remove reminder
  const removeReminder = (reminderId) => {
    let reminders = getReminders();
    reminders = reminders.filter(rem => rem.id !== reminderId);
    saveReminders(reminders);
  };

  // Clear all reminders for a plant
  const clearPlantReminders = (plantId) => {
    let reminders = getReminders();
    reminders = reminders.filter(rem => rem.plantId !== plantId);
    saveReminders(reminders);
  };

  // Diary entries methods
  const getDiaryEntries = () => {
    try {
      const entries = localStorage.getItem(DIARY_ENTRIES_KEY);
      return entries ? JSON.parse(entries) : {};
    } catch (error) {
      console.error("Error loading diary entries from localStorage:", error);
      return {};
    }
  };

  const saveDiaryEntry = (dateKey, entry) => {
    try {
      const entries = getDiaryEntries();
      entries[dateKey] = entry;
      localStorage.setItem(DIARY_ENTRIES_KEY, JSON.stringify(entries));
      window.dispatchEvent(new Event('storage'));
    } catch (error) {
      console.error("Error saving diary entry to localStorage:", error);
    }
  };

  const deleteDiaryEntry = (dateKey) => {
    try {
      const entries = getDiaryEntries();
      delete entries[dateKey];
      localStorage.setItem(DIARY_ENTRIES_KEY, JSON.stringify(entries));
      window.dispatchEvent(new Event('storage'));
    } catch (error) {
      console.error("Error deleting diary entry from localStorage:", error);
    }
  };

  // Clear all data (for reset functionality)
  const clearAllData = () => {
    localStorage.removeItem(PLANTS_STORAGE_KEY);
    localStorage.removeItem(REMINDERS_STORAGE_KEY);
    localStorage.removeItem(DIARY_ENTRIES_KEY);
    plants = [];
    notifySubscribers();
    window.dispatchEvent(new Event('storage'));
  };

  const addPlantType = (typeName, options = {}) => {
    if (!plantTypes[typeName]) {
      plantTypes[typeName] = {
        image: options.image || placeholderImg,
        size: options.size || 48
      };
    }
  };

  // Hook for React components to use garden data
  const useGardenData = () => {
    const [componentPlants, setComponentPlants] = useState(plants);
    
    useState(() => {
      // Subscribe to changes
      const unsubscribe = subscribe(updatedPlants => {
        setComponentPlants(updatedPlants);
      });
      
      // Clean up subscription
      return unsubscribe;
    }, []);

    return {
      plants: componentPlants,
      plantTypes,
      calculatePlantAge,
      addPlant,
      updatePlant,
      removePlant
    };
  };

  return {
    getPlants,
    plantTypes,
    calculatePlantAge,
    setPlants,
    addPlant,
    updatePlant,
    updatePlantProperty,
    removePlant,
    subscribe,
    useGardenData,
    addPlantType,
    // New reminder methods
    getReminders,
    addReminder,
    removeReminder,
    clearPlantReminders,
    // New diary methods
    getDiaryEntries,
    saveDiaryEntry,
    deleteDiaryEntry,
    // Utility
    clearAllData
  };
};



// Create a singleton instance
const gardenService = createGardenService();

export default gardenService;